import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  AddHubPayload,
  AddSensorPayload,
  IBananaQrDetailsRes,
  IChartDataItem,
  IEditSensorPayload,
  IHubQrDetailsRes,
  ISensorDetails,
  ISensorItem,
  ISensorQrDetailsRes,
} from '@/models';
import { celsiusToFahrenheit, http, mpaToPsi, cubeToCelinder } from '@/utils';
import { API_ENDPOINTS, LoadingKeys } from '@/constants';
import { handleApiCallInStore } from './utils';
import imageCompression from "browser-image-compression";

export interface ISensorState {
  pourSensors: ISensorItem[];
  sensorDetails: ISensorDetails;
  sensorData: {
    temperatureData: IChartDataItem[];
    strengthData: IChartDataItem[];
    displayName: string;
    location: string;
  };
  getPourSensors: (pourId: string, isFinished: boolean) => void;
  getSensorData: (pourId: number, sensorId: string, isMetric: boolean , isCylinder: boolean) => void;
  toggleEnableSensor:(
    pourId: string,
    sensorId: string,
    isDisabled: boolean,
    onSuccess: () => void
  ) => void;
  deleteSensor: (
    projectId: string,
    sensorId: string,
    onSuccess: () => void
  ) => void;
  updateSensor: () => void;
  addSensor: (
    sensorId: string,
    payload: AddSensorPayload,
    onSuccess: () => void,
    onFail: (err: Error) => void
  ) => void;
  switchDevice: (
    sensorId: string,
    banana_id: string,
    onSuccess: () => void,
    onFail: (err: Error) => void
  ) => void;
  getEditSensorDetails:(
    pourId: number,
    sensorId: string,
    onSuccess?: (data: any) => void
  ) => void;
  getSensorDetails: (
    pourId: number,
    sensorId: string,
    onSuccess?: (data: ISensorDetails) => void
  ) => void;
  getSensorIdFromQr: (
    qrLink: string,
    onSuccess: (data: ISensorQrDetailsRes) => void
  ) => void;
  getBananaIdFromQr: (
    qrLink: string,
    onSuccess: (data: IBananaQrDetailsRes) => void,
    onFail?: () => void
  ) => void;
  getHubIdFromQr: (
    qrLink: string,
    onSuccess: (data: IHubQrDetailsRes) => void
  ) => void;
  checkLifeSign: (
    sensorOrHubId: string,
    component: 'hub' | 'sensor',
    onSuccess: () => void,
    onFail: (err: Error) => void,
    onAssigned: (data:any) => void,
    onNotConnected: (data:any) => void
  ) => any;
  editSensor: (
    sensorId: string,
    data: IEditSensorPayload,
    onSuccess: () => void,
    onFail: () => void
  ) => void;
  uploadHubImage: (hubId: string, imageData: File, onFail: () => void) => void;
  addHub: (
    hubId: string,
    payload: AddHubPayload,
    onSuccess: () => void,
    onFail: (err: Error) => void
  ) => void;
}

export const useSensorsStore = create(
  persist<ISensorState>(
    (set, get) => ({
      sensorData: undefined,
      sensorDetails: undefined,
      pourSensors: [],

      getPourSensors: (pourId, isFinished) => {
        handleApiCallInStore(LoadingKeys.PourSensors, async () => {
          const { data } = await http.get<ISensorItem[]>(
            API_ENDPOINTS.project.pourSensors(pourId, isFinished)
          );
          if (data) {
            set(() => ({
              pourSensors: data,
              isFinished: isFinished
            }));
          }
        });
      },
      getSensorIdFromQr: async (qrLink, onSuccess) => {
        handleApiCallInStore(
          LoadingKeys.GetSensorIdFromQr,
          async () => {
            const { data } = await http.post<ISensorQrDetailsRes>(
              API_ENDPOINTS.project.sensorIdFromQr,
              {
                device_URI: qrLink,
              }
            );
            onSuccess?.(data);
          }
        );
      },
      getBananaIdFromQr: async (qrLink, onSuccess, onFail) => {
        handleApiCallInStore(
          LoadingKeys.GetBananaIdFromQr,
          async () => {
            const { data } = await http.post<IBananaQrDetailsRes>(
              API_ENDPOINTS.project.bananaIdFromQr,
              {
                sensor_id: qrLink,
              }
            );
            onSuccess?.(data);
          },
          undefined,
          onFail
        );
      },
      uploadHubImage: async (hubId, imageData, onFail) => {
        const options = {
            maxSizeMB: 0.1,
            maxWidthOrHeight: 1200,  // resize if needed
            useWebWorker: true
        };
        const compressedFile = await imageCompression(imageData, options);
        handleApiCallInStore(
          LoadingKeys.UploadHubImage,
          async () => {
            const formData = new FormData();
            formData.append("image", compressedFile);
            await http.put(API_ENDPOINTS.project.hubImageUpload(hubId), formData);
          },
          undefined,
          () => onFail?.()
        );
      },
      getHubIdFromQr: async (qrLink, onSuccess) => {
        handleApiCallInStore(
          LoadingKeys.GetHubIdFromQr,
          async () => {
            const { data } = await http.post<IHubQrDetailsRes>(
              API_ENDPOINTS.project.hubIdFromQr,
              {
                hub_URI: qrLink,
              }
            );
            onSuccess?.(data);
          }
        );
      },
      addSensor: async (sensorId, payload, onSuccess, onFail) => {
        handleApiCallInStore(
          LoadingKeys.AddSensor,
          async () => {
            await http.post<AddSensorPayload>(
              API_ENDPOINTS.project.addSensor(sensorId),
              payload
            );
            onSuccess?.();
          },
          undefined,
          onFail
        );
      },
      switchDevice: async (bananaId, newDevice, onSuccess, onFail) => {
        handleApiCallInStore(
          LoadingKeys.SwitchDevice,
          async () => {
            await http.post(
              API_ENDPOINTS.project.switchDevice(bananaId,newDevice),
            );
            onSuccess?.();
          },
          undefined,
          onFail
        );
      },
      addHub: async (hubId, payload, onSuccess, onFail) => {
        handleApiCallInStore(
          LoadingKeys.AddHub,
          async () => {
            await http.post<AddHubPayload>(
              API_ENDPOINTS.project.addHub(hubId),
              payload
            );
            onSuccess?.();
          },
          undefined,
          onFail
        );
      },
      editSensor: async (sensorId, data, onSuccess, onFail) => {
        handleApiCallInStore(
          LoadingKeys.EditSensor,
          async () => {
            await http.put<IEditSensorPayload>(
              API_ENDPOINTS.project.updateSensor(sensorId),
              data
            );
            if (onSuccess) {
              onSuccess?.();
            }
          },
          undefined,
          () => onFail?.()
        );
      },
      checkLifeSign: async (sensorOrHubId, component, onSuccess, onFail, onAssigned, onNotConnected) => {
        handleApiCallInStore(LoadingKeys.CheckLifeSign,
          async () => {
            const lifeSignUrl = component === 'hub'
              ? API_ENDPOINTS.project.checkHubLifeSign(sensorOrHubId)
              : API_ENDPOINTS.project.checkSensorLifeSign(sensorOrHubId);
            const { data } = await http.get<{ exist: boolean, active: boolean }>(
              lifeSignUrl
            );
            if (data?.exist) {
              if(data?.active){
                onAssigned?.(data);
              }else{
              onSuccess?.();
              }
            } else {
              onNotConnected?.(data)
            }
          },
          undefined,
          onFail  
        )
        
      },

      getEditSensorDetails: (pourId, sensorId, onSuccess) => 
        handleApiCallInStore(LoadingKeys.GetSensorDetails, async () => {
          const { data } = await http.get<ISensorDetails>(
            API_ENDPOINTS.project.geteditsensordetails(pourId,sensorId)
          );
          if (onSuccess) {
            onSuccess(data);
          }
          set(() => ({
            sensorDetails: data,
          }));
        }),

      getSensorDetails: async (pourId, sensorId, onSuccess) =>
        handleApiCallInStore(LoadingKeys.GetSensorDetails, async () => {
          const { data } = await http.get<ISensorDetails>(
            API_ENDPOINTS.project.sensorDetails(pourId,sensorId)
          );
          if (onSuccess) {
            onSuccess(data);
          }
          set(() => ({
            sensorDetails: data,
          }));
        }),

      getSensorData: async (pourId, sensorId, isMetric,isCylinder) => {
        handleApiCallInStore(LoadingKeys.GetSensorDetails, async () => {
          const { data } = await http.get<ISensorDetails>(
            API_ENDPOINTS.project.sensorDetails(pourId, sensorId)
          );
          let temperatureData = [];
          let strengthData = [];
          let resistivityData = [];
          data?.concrete_strength.forEach(sensorItem => {
            if (sensorItem.strength_derivative_1 !== null || sensorItem.strength_derivative_1 !== undefined) {
              strengthData.push({
                date: sensorItem.sensor_ts,
                value: isMetric
                  ? isCylinder? cubeToCelinder(sensorItem.strength_derivative_1)?.toFixed(2): sensorItem.strength_derivative_1?.toFixed(2)
                  : isCylinder?  mpaToPsi(cubeToCelinder(sensorItem.strength_derivative_1))?.toFixed(2) :  mpaToPsi(sensorItem.strength_derivative_1)?.toFixed(2)
              });
            }
          });
          data?.sensor_data.forEach(sensorItem => {
              temperatureData.push({
                date: sensorItem.sensor_ts,
                value: sensorItem.temperature_from_resistance_1 == null? null:
                  isMetric
                  ? sensorItem.temperature_from_resistance_1?.toFixed(2)
                  : celsiusToFahrenheit(
                      sensorItem.temperature_from_resistance_1
                    )?.toFixed(2),
              });
            
          });
          set(() => ({
            sensorData: {
              displayName: data?.sensor_attache?.[0]?.name,
              location:data?.sensor_attache?.[0].location,
              temperatureData,
              strengthData,
              resistivityData
            },
          }));
        });
      },

      toggleEnableSensor: async (pourId, sensorId, isDisabled ,onSuccess) =>
        handleApiCallInStore(LoadingKeys.DeleteSensor, async () => {
          await http.post(
            API_ENDPOINTS.project.toggleEnableSensor(pourId, sensorId),
            {
              isDisable: isDisabled
            }
          );
          await onSuccess();
          const filteredPours = get().pourSensors;
          filteredPours[sensorId].is_disabled = !filteredPours[sensorId].is_disabled;

          set(() => ({
            pourSensors: { ...filteredPours },
          }));
        }),


      deleteSensor: async (projectId, sensorId, onSuccess) =>
        handleApiCallInStore(LoadingKeys.DeleteSensor, async () => {
          await http.delete(
            API_ENDPOINTS.project.deleteSensor(projectId, sensorId)
          );
          await onSuccess();
          const filteredPours = get().pourSensors;
          delete filteredPours[sensorId];

          set(() => ({
            pourSensors: { ...filteredPours },
          }));
        }),

      updateSensor: async () => {},
    }),
    {
      name: 'Sensors-storage',
      getStorage: () => sessionStorage,
    }
  )
);


